class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // Instant feedback: open the drawer with a loading overlay while /cart/add is still in flight.
  beginAdding() {
    this.classList.add('is-adding');
    this._addingWasClosed = !this.classList.contains('active');
    clearTimeout(this._addingTimer);
    this._addingTimer = setTimeout(() => this.endAdding(), 30000);
    this.open(undefined, true);
  }

  endAdding(closeIfOpenedByUs = false) {
    clearTimeout(this._addingTimer);
    this.classList.remove('is-adding');
    if (closeIfOpenedByUs && this._addingWasClosed && this.classList.contains('active')) this.close();
    this._addingWasClosed = false;
  }

  open(triggeredBy, quiet = false) {
    if (!quiet) this.checkAndHideCrossSell();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (!this.classList.contains('is-empty')) {
      // updateThreshold(); 已不再使用 JavaScript 更新
    }
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  checkAndHideCrossSell() {
    const cross_sell_variant = document.querySelector('.cross_sell_variant_id');
    if (!cross_sell_variant) {
      return;
    }
    const cart_items = [];
    (window.aunyraCartJson ? window.aunyraCartJson() : fetch('/cart.js').then((response) => response.json()))
      .then((data) => {
        data.items.forEach((item) => {
          cart_items.push(parseInt(item.id));
        });
      })
      .finally(() => {
        //console.log("cart-drawer.js: fetch completed", cart_items);
        const cross_sell_wrapper = document.getElementById('cross_sell');
        const cross_sell_id = parseInt(cross_sell_variant.value);
        if (!cart_items.includes(cross_sell_id)) {
          cross_sell_wrapper.style.display = 'block';
        }
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-adding');
    clearTimeout(this._addingTimer);
    this._addingWasClosed = false;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Re-read cart state from the server and repaint drawer + header bubble.
  // Used when an add/change request fails or times out but may still have succeeded server-side.
  resync(openIfItems = true) {
    if (this._resync) return this._resync;
    const ids = this.getSectionsToRender().map((section) => section.id).join(',');
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (window.aunyraCartJson) window.aunyraCartJson.invalidate();
    this._resync = fetch(`${root}?sections=${ids}&_=${Date.now()}`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((sections) => {
        const drawerHtml = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const isEmpty = !!drawerHtml.querySelector('cart-drawer.is-empty');
        this.classList.toggle('is-empty', isEmpty);
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || !sections[section.id]) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
        });
        if (isEmpty) this.endAdding(true);
        if (openIfItems && !isEmpty) {
          setTimeout(() => {
            this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
            this.open();
          });
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this._resync = null;
        this.classList.remove('is-adding');
      });
    return this._resync;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
